import type { DutchVerb, VerbProgress } from '@homeapp/shared';

interface Props {
  verbs: (DutchVerb & { progress?: VerbProgress })[];
  onDelete?: (verbId: string) => void;
  loading?: boolean;
}

export default function VerbList({ verbs, onDelete, loading }: Props) {
  if (loading) {
    return <div className="text-center text-gray-500 py-8">Loading verbs...</div>;
  }

  if (verbs.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p className="text-lg mb-2">No verbs yet</p>
        <p className="text-sm">Use the "Add New Verb" tool above to add some!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold text-gray-900">
        Verb Database ({verbs.length})
      </h3>
      <div className="divide-y divide-gray-100">
        {verbs.map((verb) => (
          <div key={verb.verbId} className="py-3 flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{verb.infinitive}</span>
                <span className="text-sm text-gray-500">({verb.english})</span>
                <span className="text-xs">
                  {Array.from({ length: verb.difficulty }, (_, i) => (
                    <span key={i}>⭐</span>
                  ))}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {verb.separable && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">separable</span>}
                {verb.irregular && <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">irregular</span>}
                <span>aux: {verb.auxiliary}</span>
                {verb.progress && (
                  <>
                    <span className="text-green-600">✓ {verb.progress.correctCount}</span>
                    <span className="text-red-600">✗ {verb.progress.incorrectCount}</span>
                    {verb.progress.correctCount + verb.progress.incorrectCount > 0 && (
                      <span>
                        ({Math.round((verb.progress.correctCount / (verb.progress.correctCount + verb.progress.incorrectCount)) * 100)}%)
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            {onDelete && (
              <button
                onClick={() => { if (confirm(`Delete "${verb.infinitive}"?`)) onDelete(verb.verbId); }}
                className="text-red-400 hover:text-red-600 ml-2 p-1"
                title="Delete verb"
              >
                🗑
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
